import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Trash2, Lock, Unlock, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CEFR_LEVELS, COMMON_LANGUAGES, type CEFRLevel } from "@/types/candidate";

interface LanguageEntry {
  id?: string;
  language: string;
  language_code: string;
  level: CEFRLevel;
  is_user_owned: boolean;
  is_mandatory?: boolean; // For job languages
}

interface LanguageManagerProps {
  languages: LanguageEntry[];
  onChange: (languages: LanguageEntry[]) => void;
  type: 'candidate' | 'job';
  disabled?: boolean;
}

export const LanguageManager = ({
  languages,
  onChange,
  type,
  disabled = false
}: LanguageManagerProps) => {
  const { t } = useTranslation();
  const [newLanguageCode, setNewLanguageCode] = useState('');
  const [newLevel, setNewLevel] = useState<CEFRLevel>(3); // Default to B1
  const [newIsMandatory, setNewIsMandatory] = useState(true); // For jobs

  const handleAddLanguage = () => {
    if (!newLanguageCode.trim()) return;

    const languageName = COMMON_LANGUAGES[newLanguageCode as keyof typeof COMMON_LANGUAGES];
    if (!languageName) return;

    const newEntry: LanguageEntry = {
      language: languageName,
      language_code: newLanguageCode.toLowerCase(),
      level: newLevel,
      is_user_owned: true,
      ...(type === 'job' && { is_mandatory: newIsMandatory })
    };

    onChange([...languages, newEntry]);

    // Reset form
    setNewLanguageCode('');
    setNewLevel(3);
    setNewIsMandatory(true);
  };

  const handleRemoveLanguage = (index: number) => {
    const updatedLanguages = languages.filter((_, i) => i !== index);
    onChange(updatedLanguages);
  };

  const handleUpdateLanguage = (index: number, updates: Partial<LanguageEntry>) => {
    const updatedLanguages = languages.map((lang, i) =>
      i === index ? { ...lang, ...updates } : lang
    );
    onChange(updatedLanguages);
  };

  const handleReleaseLock = (index: number) => {
    handleUpdateLanguage(index, { is_user_owned: false });
  };

  const handleLevelChange = (index: number, level: CEFRLevel) => {
    handleUpdateLanguage(index, { level, is_user_owned: true });
  };

  const getLanguageName = (code: string) => {
    return COMMON_LANGUAGES[code as keyof typeof COMMON_LANGUAGES] || code.toUpperCase();
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-sm font-medium">
          {type === 'candidate' ? t('candidates.languages.title', 'Languages') : t('jobs.languages.title', 'Language Requirements')}
        </Label>

        {/* Existing languages */}
        <div className="space-y-2">
          {languages.map((lang, index) => (
            <div key={lang.id || index} className="flex items-center gap-2 p-2 border rounded-md">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{lang.language} ({lang.language_code.toUpperCase()})</span>
                  {lang.is_user_owned && (
                    <Badge variant="secondary" className="text-xs">
                      <Lock className="w-3 h-3 mr-1" />
                      {t('common.userOwned', 'User Edited')}
                    </Badge>
                  )}
                  {type === 'job' && (
                    <Badge variant={lang.is_mandatory ? "default" : "outline"} className="text-xs">
                      {lang.is_mandatory ? t('jobs.languages.mandatory', 'Mandatory') : t('jobs.languages.optional', 'Optional')}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <Select
                    value={lang.level.toString()}
                    onValueChange={(value) => handleLevelChange(index, parseInt(value) as CEFRLevel)}
                    disabled={disabled}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(CEFR_LEVELS).map(([level, label]) => (
                        <SelectItem key={level} value={level}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {type === 'job' && (
                    <Select
                      value={lang.is_mandatory ? 'mandatory' : 'optional'}
                      onValueChange={(value) => handleUpdateLanguage(index, { is_mandatory: value === 'mandatory' })}
                      disabled={disabled}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mandatory">{t('jobs.languages.mandatory', 'Mandatory')}</SelectItem>
                        <SelectItem value="optional">{t('jobs.languages.optional', 'Optional')}</SelectItem>
                      </SelectContent>
                    </Select>
                  )}

                  {lang.is_user_owned && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleReleaseLock(index)}
                      disabled={disabled}
                      title={t('common.releaseLock', 'Release user lock - AI can modify')}
                    >
                      <Unlock className="w-4 h-4" />
                    </Button>
                  )}

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleRemoveLanguage(index)}
                    disabled={disabled}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Add new language */}
        <div className="border-t pt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div>
              <Label htmlFor="new-language-select" className="text-xs">
                {t('common.selectLanguage', 'Select Language')}
              </Label>
              <Select
                value={newLanguageCode}
                onValueChange={setNewLanguageCode}
                disabled={disabled}
              >
                <SelectTrigger id="new-language-select">
                  <SelectValue placeholder={t('common.selectLanguagePlaceholder', 'Choose a language')} />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(COMMON_LANGUAGES)
                    .filter(([code]) => !languages.some(l => l.language_code === code))
                    .map(([code, name]) => (
                      <SelectItem key={code} value={code}>
                        {name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="new-language-level" className="text-xs">
                {t('common.proficiencyLevel', 'Proficiency Level')}
              </Label>
              <Select
                value={newLevel.toString()}
                onValueChange={(value) => setNewLevel(parseInt(value) as CEFRLevel)}
                disabled={disabled}
              >
                <SelectTrigger id="new-language-level">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CEFR_LEVELS).map(([level, label]) => (
                    <SelectItem key={level} value={level}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              {type === 'job' && (
                <div className="mr-2 flex-1">
                  <Label htmlFor="new-is-mandatory" className="text-xs">
                    {t('common.requirement', 'Requirement')}
                  </Label>
                  <Select
                    value={newIsMandatory ? 'mandatory' : 'optional'}
                    onValueChange={(value) => setNewIsMandatory(value === 'mandatory')}
                    disabled={disabled}
                  >
                    <SelectTrigger id="new-is-mandatory" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mandatory">{t('jobs.languages.mandatory', 'Mandatory')}</SelectItem>
                      <SelectItem value="optional">{t('jobs.languages.optional', 'Optional')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <Button
                type="button"
                onClick={handleAddLanguage}
                disabled={disabled || !newLanguageCode.trim()}
                className="mb-0"
              >
                <Plus className="w-4 h-4 mr-1" />
                {t('common.add', 'Add')}
              </Button>
            </div>
          </div>
        </div>

        {languages.length === 0 && (
          <p className="text-sm text-gray-500 italic">
            {type === 'candidate'
              ? t('candidates.languages.noLanguages', 'No languages specified')
              : t('jobs.languages.noLanguages', 'No language requirements for this job')
            }
          </p>
        )}
      </div>
    </div>
  );
};